interface PendingApproval {
  type: string
  project: string
  title: string
  daysWaiting: number
  priority: 'Critical' | 'High' | 'Medium'
}

interface LeadershipActionCenterProps {
  pendingApprovals: PendingApproval[]
  onReview?: (approval: PendingApproval) => void
  onApprove?: (approval: PendingApproval) => void
  onReject?: (approval: PendingApproval) => void
}

export function LeadershipActionCenter({
  pendingApprovals,
  onReview,
  onApprove,
  onReject,
}: LeadershipActionCenterProps) {
  const hasPending = pendingApprovals.length > 0

  return (
    <div className={`p-6 rounded-xl border-2 shadow-lg ${
      hasPending
        ? 'bg-gradient-to-br from-red-50 via-orange-50 to-red-50 border-red-300'
        : 'bg-white border-[#bcccdc]'
    }`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`p-2 rounded-lg ${hasPending ? 'bg-red-100' : 'bg-[#f0f4f8]'}`}>
          <svg className={`w-6 h-6 ${hasPending ? 'text-red-600' : 'text-[#486581]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl text-[#102a43]">Leadership Action Center</h2>
        <span className={`ml-2 px-3 py-1 rounded-full text-xs font-medium ${
          hasPending ? 'bg-red-600 text-white' : 'bg-[#f0f4f8] text-[#486581]'
        }`}>
          {pendingApprovals.length} Pending
        </span>
      </div>
      {!hasPending && (
        <p className="text-sm text-[#829ab1] py-2">No pending approvals or decisions at this time.</p>
      )}
      <div className="space-y-3">
        {pendingApprovals.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-4 p-4 bg-white border-2 border-[#bcccdc] rounded-lg shadow-sm hover:shadow-md hover:border-[#829ab1] transition-all"
          >
            <div
              className={`px-3 py-1 rounded text-sm font-medium ${
                item.priority === 'Critical'
                  ? 'bg-red-100 text-red-700 border border-red-300'
                  : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
              }`}
            >
              {item.type}
            </div>
            <div className="flex-1">
              <h3 className="font-medium mb-1 text-[#102a43]">{item.title}</h3>
              <p className="text-sm text-[#486581]">{item.project}</p>
            </div>
            <div className="text-sm text-[#829ab1] bg-[#f0f4f8] px-3 py-1 rounded">
              Waiting {item.daysWaiting} days
            </div>
            <div className="flex gap-2">
              {onReview && (
                <button
                  onClick={() => onReview(item)}
                  className="px-4 py-2 border-2 border-[#bcccdc] text-[#486581] rounded-lg hover:bg-[#f0f4f8] transition-colors"
                >
                  Review
                </button>
              )}
              {onApprove && (
                <button
                  onClick={() => onApprove(item)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Approve
                </button>
              )}
              {onReject && (
                <button
                  onClick={() => onReject(item)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Reject
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

